import { useEffect } from 'react';
import { SubscribeToMoreOptions, WatchQueryFetchPolicy } from '@apollo/client';
// Api
import { ON_NOTIFICATION_CREATED } from 'api/notifications/subscriptions';
import apolloNotificationClient from 'api/apolloNotificationClient';
// Types
import {
  OnNotificationCreated,
  OnNotificationCreated_onNotificationCreated,
} from 'api/notifications/types/OnNotificationCreated';

type NotificationWithId = {
  id: string;
  [key: string]: any;
};

export const commonOptions = (
  skip: boolean,
  limit: number,
  nextToken: string | null
) => ({
  client: apolloNotificationClient,
  variables: {
    input: {
      limit,
      nextToken,
    },
  },
  skip,
  fetchPolicy: 'cache-and-network' as WatchQueryFetchPolicy,
  notifyOnNetworkStatusChange: false,
});

/**
 * Hook that handles notification subscription
 *
 * @param subscribeToMore - The subscribeToMore function from useQuery
 * @param queryData - The data object from the query result
 * @param userId - The current user's ID
 * @param queryResultKey - The key in the query result (e.g., 'getNotifications')
 * @param filterFn - Optional filter function to decide if a notification should be added
 * @param typecastFn - Optional function to cast the notification to the correct type
 */
export function useNotificationSubscription<
  TData,
  TNotificationData extends NotificationWithId
>(
  subscribeToMore: (options: SubscribeToMoreOptions<TData, any>) => () => void,
  queryData: TData | undefined,
  userId: string | undefined,
  queryResultKey: string,
  filterFn?: (
    notification: OnNotificationCreated_onNotificationCreated | null
  ) => boolean,
  typecastFn?: (
    notification: OnNotificationCreated_onNotificationCreated
  ) => TNotificationData
): void {
  useEffect(() => {
    // Only set up subscription if we have userId and after initial query
    if (!userId || !queryData) return;

    // Subscribe to new notifications
    const unsubscribe = subscribeToMore({
      document: ON_NOTIFICATION_CREATED,
      variables: { userId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        // Extract the new notification
        const subData = (subscriptionData.data as unknown) as OnNotificationCreated;
        const newNotification = subData.onNotificationCreated;
        if (!newNotification) return prev;

        // Apply custom filter if provided
        if (filterFn && !filterFn(newNotification)) return prev;

        // Safely access the query result data
        const prevResult = prev[queryResultKey];
        if (!prevResult || !prevResult.data) return prev;

        // Prevent duplicates
        const exists = prevResult.data.some(
          (notification: any) => notification.id === newNotification.id
        );
        if (exists) return prev;

        // Use typecast function if provided, otherwise use the notification as is
        const typedNotification = typecastFn
          ? typecastFn(newNotification)
          : ((newNotification as unknown) as TNotificationData);

        // Add the new notification to the beginning of the list
        return {
          ...prev,
          [queryResultKey]: {
            ...prevResult,
            data: [typedNotification, ...prevResult.data],
          },
        };
      },
    });

    // Clean up subscription on unmount
    return () => unsubscribe();
  }, [
    queryData,
    userId,
    subscribeToMore,
    queryResultKey,
    filterFn,
    typecastFn,
  ]);
}
